import {
  currencyFractionDigits,
  minorUnitsToDisplayAmount,
} from "./zeroDecimalCurrencies";
import { formatNumber } from "@/i18n";

export interface PublicStripePrice {
  stripePriceId: string;
  /**
   * Сумма в НАИМЕНЬШЕЙ единице валюты — а это не всегда сотая доля.
   *
   * WEB-1469: прежняя подпись «smallest currency unit (cents)» утверждала
   * неправду и провоцировала повторение ошибки в новом коде: у иены, воны,
   * донга и ещё полутора десятков валют наименьшая единица — САМА валюта,
   * поэтому 1200 значит ¥1200, а не ¥12. Делить на сто по месту нельзя:
   * делитель выбирается по валюте (см. utils/zeroDecimalCurrencies.ts —
   * `minorUnitsToDisplayAmount`, справочник держится сторожем паритета с
   * бэкендовым `STRIPE_ZERO_DECIMAL_CURRENCIES`).
   */
  unitAmount: number;
  currency: string;
  /** 'ONE_TIME' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'. */
  interval: string;
  intervalCount?: number;
  trialPeriodDays?: number;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: "$",
  eur: "€",
  gbp: "£",
  rub: "₽",
  // WEB-1607: у иены и воны знак узнаётся покупателем быстрее трёхбуквенного кода.
  jpy: "¥",
  krw: "₩",
};

/**
 * Знак валюты по её коду. Неизвестная валюта печатается кодом с пробелом
 * («PLN 10.00») — это честнее выдуманного символа.
 *
 * WEB-1611: вынесено из `formatPriceAmount`, потому что тот же знак теперь
 * нужен плашкам аналитики (см. utils/revenueMetric.ts). Второй таблицы знаков
 * заводить нельзя: она разъедется с этой, и один и тот же проект показал бы
 * покупателю «¥1,000» на пейволле, а владельцу «JPY 1,000» в кабинете.
 */
export function currencySymbol(currency?: string | null): string {
  const code = (currency ?? "").toLowerCase();
  return CURRENCY_SYMBOLS[code] ?? `${(currency ?? "").toUpperCase()} `;
}

const INTERVAL_SHORT: Record<string, string> = {
  DAY: "day",
  WEEK: "wk",
  MONTH: "mo",
  YEAR: "yr",
};

/**
 * Сумма в минорных единицах → человеческая строка с валютой, БЕЗ периода.
 *
 * WEB-925 атом 5: вынесено из `formatStripePrice`, чтобы режимы подачи цены
 * (`derivePriceDisplay`) печатали пересчитанную сумму той же таблицей валют.
 * Своя копия таблицы в соседнем файле рано или поздно разъехалась бы — и
 * покупатель увидел бы одну и ту же цену в двух разных валютных написаниях.
 *
 * WEB-1607: делитель выбирается ПО ВАЛЮТЕ, а не всегда сто. Stripe хранит
 * сумму в наименьших единицах, и у иены, воны и ещё полутора десятков валют
 * эта единица целая — прежнее безусловное деление показывало японскому
 * покупателю «JPY 10.00» там, где цена ¥1000. Справочник валют — копия
 * бэкендового, держится сторожем паритета (см. zeroDecimalCurrencies.ts).
 *
 * Разделитель тысяч ставится ТОЛЬКО у валют без дробной части: у остальных вид
 * сложился до этой карточки («$29.97», «$1234.56») и менять его тут значило бы
 * протащить косметику под видом починки денег, а у иены прежнего вида не было
 * вовсе — там печаталась неправда, и «¥100,000» читается, а «¥100000» нет.
 *
 * WEB-1469: сами цифры считает `formatAmountDigits` ниже — здесь осталось
 * только «знак + цифры». Вид печати не изменился ни для одной валюты.
 */
export function formatPriceAmount(
  minorUnits: number,
  currency: string,
): string {
  const code = (currency ?? "").toLowerCase();
  return `${currencySymbol(code)}${formatAmountDigits(minorUnits, code)}`;
}

/**
 * ТОЛЬКО ЦИФРЫ суммы, без знака валюты, по тому же правилу выбора делителя.
 *
 * WEB-1469: понадобилось местам, которые печатают сумму рядом с ТРЁХБУКВЕННЫМ
 * кодом валюты, а не со знаком, — выбор цены в конструкторе и каталог Stripe
 * в настройках («JPY 1,200 / YEAR»). Оба держали своё «(unitAmount / 100)
 * .toFixed(2)» и показывали владельцу иену в сто раз меньше настоящей: он
 * сверял каталог с кабинетом Stripe и не мог понять, какая строка правдивая.
 *
 * Заводить им третью копию правила было нельзя — копии расходятся молча
 * (ровно так разъехались когорты в WEB-1626), поэтому цифры вынесены сюда, а
 * `formatPriceAmount` осталась «знак + эти же цифры». Виды печати совпадают
 * с прежними для двухдесятичных валют дословно.
 */
export function formatAmountDigits(
  minorUnits: number,
  currency?: string | null,
): string {
  const code = (currency ?? "").toLowerCase();
  // WEB-2655: число знаков — из общего правила (иена 0, динар 3, прочие 2), а
  // не «иена 0, остальные 2» по месту: так динар печатался «199.90» вместо
  // «19.990».
  const fractionDigits = currencyFractionDigits(code);
  if (fractionDigits === 0) {
    // WEB-1874: строка локали живёт в i18n/format.ts; пин английской раскладки
    // и число знаков после запятой прежние — цифры не изменились.
    return formatNumber(
      minorUnits,
      { minimumFractionDigits: 0, maximumFractionDigits: 0 },
      'en',
    );
  }
  // WEB-1626: делитель — общая функция, а не записанное по месту «/ 100»
  // (та же копия правила в когортах показывала иену в сто раз меньше).
  return minorUnitsToDisplayAmount(minorUnits, code).toFixed(fractionDigits);
}

/** Минимум полей цены, из которых складывается подпись строки каталога. */
export interface StripeCatalogPriceLike {
  stripePriceId: string;
  productName?: string | null;
  unitAmount: number;
  currency: string;
  interval: string;
}

/**
 * Подпись цены В СПИСКЕ КАТАЛОГА проекта: «Премиум — USD 9.99 / MONTH».
 *
 * Одна дверь на обе витрины кабинета, которые показывают один и тот же каталог
 * одного проекта: выбор цены в редакторе пейвола (`StripePriceSelector`) и
 * пара «цена → уровень доступа» в подключении приложения
 * (`AppConnectionSection`, WEB-1930). Раньше подпись жила прямо в разметке
 * селектора редактора; вторая витрина обязана была бы завести копию, а копии
 * денежных правил в этом проекте расходились уже дважды (WEB-1469, WEB-1626) —
 * и владелец видел одну и ту же цену двумя разными строками. Совпадение витрин
 * держит сторож `priceCatalogLabel.twoEnds.web1930.guard.test.ts`.
 *
 * Название товара впереди: без него несколько цен с одинаковой суммой
 * («USD 9.99 / MONTH» ×3) в списке неотличимы. Делитель суммы выбирается ПО
 * ВАЛЮТЕ (`formatAmountDigits`), а не безусловным «/ 100» — см. WEB-1469.
 */
export function formatStripePriceCatalogLabel(
  price: StripeCatalogPriceLike,
): string {
  const amount = `${price.currency.toUpperCase()} ${formatAmountDigits(
    price.unitAmount,
    price.currency,
  )} / ${price.interval}`;
  return price.productName ? `${price.productName} — ${amount}` : amount;
}

/** Human-readable price label, e.g. "$9.99/mo" or "€5.00". */
export function formatStripePrice(price: PublicStripePrice): string {
  const base = formatPriceAmount(price.unitAmount, price.currency);

  if (!price.interval || price.interval === "ONE_TIME") {
    return base;
  }
  const unit = INTERVAL_SHORT[price.interval] ?? price.interval.toLowerCase();
  const count =
    price.intervalCount && price.intervalCount > 1
      ? `${price.intervalCount} `
      : "";
  return `${base}/${count}${unit}`;
}

/** Find a price in a list by its Stripe price id. */
export function findStripePrice(
  prices: PublicStripePrice[] | undefined,
  id?: string,
): PublicStripePrice | undefined {
  if (!prices || !id) return undefined;
  return prices.find((p) => p.stripePriceId === id);
}
